import {
    ClaimListFilters,
    ClaimStatuses,
    ClaimTypes,
    TravelClaimForList,
    TravelClaimListUpdateModel,
} from '../../types/travelClaims';
import { Loading } from '../Loading';
import { Pagination } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE } from '../../helpers/pagination';
import MessagesBlackIcon from '../../assets/chat_messages_black.svg';
import { useTravelClaimsQueryV2 } from '../../hooks/useTravelClaimsQuery';
import BellYellow from '../../assets/bell_yellow.svg';
import BellGrey from '../../assets/bell_grey.svg';
import LookingGlass from '../../assets/looking_glass.svg';
import OrderingDefault from '../../assets/ordering_default.svg';
import OrderingAsc from '../../assets/ordering_asc.svg';
import OrderingDesc from '../../assets/ordering_desc.svg';
import GlobePlaneIcon from '../../assets/globe_plane.svg';
import Circle from '../../assets/circle.svg';
import ISIcon from '../../assets/is.svg';
import EmptyPlaceholder from '../_layout/EmptyPlaceholder';
import { useNavigate } from 'react-router-dom';
import { getClaimTypeNameShort } from '../../helpers/claims';
import { getClaimStatus, getUnreadMessages } from './itemComponents/Statuses';
import { filterUsersByIds, getUserFioShort } from '../../helpers/users';
import { Dictionary } from '../../types/interfaces/dictionary';
import DeleteIcon from '../../assets/delete.svg'
import {
    TravelClaimListContextMenu,
    TravelClaimListContextMenuHandle
} from './listComponents/TravelClaimListContextMenu';
import { postponeClaimRequest, returnToNew, setInWork, updateTravelClaim } from '../../services/travelClaims';
import { ColleagueInlineAvatarList } from './listComponents/ColleagueInlineAvatarList';
import { ColleaguePopupAvatarList, ColleaguePopupAvatarListHandle } from './listComponents/ColleaguePopupAvatarList';
import { debouncedSearch, shortenText } from "../../helpers/text";
import { AllOrNewCheckbox } from "./listComponents/ClaimListCheckbox";
import { CommonWebsocket } from "../CommonWebsocket";
import { UserWebsocket } from "../UserWebsocket";
import { WebsocketMessageData, WebsocketMessagesTypes } from "../../types/websockets";
import { useUsersByIdListQuery } from "../../hooks/useUsersQuery";
import { useUserContext } from "../../context/user";
import { RoundSlider } from "../RoundSlider";
import { formatDates } from "../../helpers/dates"
import { useBadge } from "../../context/badge";

type TravelClaimListProps = {
    statusFilter: ClaimStatuses[] | null;
};

export const TravelClaimList = ({ statusFilter }: TravelClaimListProps) => {
    const { showMyClaims, setShowMy, showNewClaims, setShowNew, userUuid } = useUserContext();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { setNewClaimsBadgeValue } = useBadge();
    const [ordering, setOrdering] = useState<Dictionary<string | undefined>>({ 'updated_at': '-' });
    const [searchText, setSearchText] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<ClaimListFilters>(() => {
        let init: ClaimListFilters = {
            createdAtFrom: null,
            createdAtTo: null,
            responsible: null,
            types: []
        }
        if (showMyClaims) {
            init.responsible = userUuid
        }
        // Если на общей вкладке и показывать новые
        if (!statusFilter && showNewClaims) {
            init.status = 'NEW'
        }
        return init;
    });
    const [focusedClaim, setFocusedClaim] = useState<number>();
    const [usersPkOnPage, setUsersPkOnPage] = useState<Array<string>>([]);

    const contextMenuRef = useRef<TravelClaimListContextMenuHandle>(null);
    const colleaguesListPopupRef = useRef<ColleaguePopupAvatarListHandle>(null);

    const TABLE_HEAD = [
        // Title, ordering param
        ['№ Заявки', 'pk'],
        ['Название', ''],
        ['Дата поездки', ''],
        ['Чаты', ''],
        ['Пассажир', ''],
        ['Инициатор', 'author'],
        ['Дата создания', 'created_at'],
        ['Статус', 'status'],
        ['Ответственный', 'responsible'],
        ['Тип', 'type'],
    ];

    const {
        isLoading,
        isSuccess,
        isError,
        data,
        refetch: refetchClaims,
        // } = useTravelClaimsQuery(statusFilter, ordering, search, filters, page);
    } = useTravelClaimsQueryV2(statusFilter, ordering, searchQuery, filters, page);

    const {
        data: newClaimsData,
        refetch: refetchNewClaims,
    } = useTravelClaimsQueryV2(['NEW'], '-', undefined, filters, 1, 1);

    useEffect(() => {
        if (newClaimsData?.count) {
            setNewClaimsBadgeValue(newClaimsData.count)
        }
    }, [newClaimsData]);

    const { mutate: updateClaim } = useMutation({
        mutationFn: ({ pk, model }: { pk: string, model: TravelClaimListUpdateModel }) => updateTravelClaim(pk, model),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        }
    });


    const { mutate: returnToNewMutation } = useMutation({
        mutationFn: ({ pk }: { pk: string }) => returnToNew(pk),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        }
    });

    const { mutate: postponeClaimMutation } = useMutation({
        mutationFn: ({ pk }: { pk: string }) => postponeClaimRequest(pk),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        }
    });

    const { mutate: setInWorkMutation } = useMutation({
        mutationFn: ({ pk }: { pk: string }) => setInWork(pk),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        }
    });

    const updateOrdering = (field: string) => {
        setOrdering(prevState => {
            const currentSort = prevState[field];
            let newSort: '' | '-' | undefined = undefined;
            if (currentSort === undefined) { // Если не установлена, то ставим по возрастанию
                newSort = '';
                // console.log('currentSort undefined')
                // console.log('newSort asc')
            } else if (currentSort === '') { // Если по возрастанию, то ставим по убыванию
                newSort = '-';
                // console.log('currentSort asc')
                // console.log('newSort desc')
            } else if (currentSort === '-') { // Если по убыванию, то убираем
                newSort = undefined;
                // console.log('currentSort desc')
                // console.log('newSort undefined')
            }

            // Делаем сортировку по 1 полю, по нескольким происходит путаница со стороны пользователя
            if (newSort === undefined) {
                return { 'updated_at': '-' }
            }
            return {
                // ...prevState,
                [field]: newSort,
            };
        });
    }

    const getOrderingIcon = (field: string) => {
        if (ordering[field] === undefined) {
            return OrderingDefault;
        }

        if (ordering[field] === '') {
            return OrderingAsc;
        }

        if (ordering[field] === '-') {
            return OrderingDesc;
        }
    }

    const getPageName = () => {
        switch (statusFilter) {
            case ['DRAFT']:
                return 'Черновики';
            case ['NEW']:
                return 'Новые';
            case ['IN_WORK', 'REGISTERED']:
                return 'В работе';
            case ['REGISTERED']:
                return 'Оформленные';
            case ['FINISHED']:
                return 'Завершенные';
            case ['POSTPONED']:
                return 'Отложенные';
            default:
                return 'Все заявки';
        }
    }

    const getTypeClass = (status: ClaimTypes) => {
        switch (status) {
            case 'SIMPLE':
                return 'filter-red';
            case 'COMPLEX':
                return 'filter-blue';
            case 'VISE':
                return 'filter-yellow';
            default:
                return 'opacity-0';
        }
    }

    const getClaimDates = (claim: TravelClaimForList) => {
        let started_at = ''
        let finished_at = ''
        if (claim.type === 'SIMPLE') {
            started_at = claim.claim_started_at ? claim.claim_started_at : ''
            finished_at = claim.claim_finished_at ? claim.claim_finished_at : ''
        }

        if (claim.type === 'COMPLEX') {
            // started_at = claim.created_at
        }
        if (claim.type === 'VISE') {
            started_at = claim.entry_date ? claim.entry_date : ''
            finished_at = claim.departure_date ? claim.departure_date : ''
        }
        return formatDates(started_at, finished_at)
    }

    const getClaimName = (claim: TravelClaimForList) => {
        let _name = claim.claim_name || '';
        // Закоменчено по #53127
        // if (claim.type === 'COMPLEX') {
        //     _name = claim.details || ''
        // }

        return shortenText(_name, 32)
    }

    const toggleClaimIsImportant = (claim: TravelClaimForList) => {
        updateClaim({
            pk: claim.pk.toString(),
            model: {
                important: !claim.important
            }
        })
    }

    const toggleClaimIsAbroad = (claim: TravelClaimForList) => {
        updateClaim({
            pk: claim.pk.toString(),
            model: {
                is_abroad: !claim.is_abroad
            }
        })
    }

    const toggleClaimNonresidentColleagues = (claim: TravelClaimForList) => {
        updateClaim({
            pk: claim.pk.toString(),
            model: {
                nonresident_colleagues: !claim.nonresident_colleagues
            }
        })
    }

    const updateComment = (claim: TravelClaimForList, text: string) => {
        updateClaim({
            pk: claim.pk.toString(),
            model: {
                responsible_comment: text
            }
        })
    }

    const returnClaimToNew = (claim: TravelClaimForList) => {
        returnToNewMutation({
            pk: claim.pk.toString(),
        })
    }

    const postponeClaim = (claim: TravelClaimForList) => {
        postponeClaimMutation({
            pk: claim.pk.toString(),
        })
    }

    const setClaimInWork = (claim: TravelClaimForList) => {
        setInWorkMutation({
            pk: claim.pk.toString(),
        })
    }

    const handleShowMy = (value: boolean) => {
        // Если установлен true, то передаём в фильтры uuid ответственного, иначе - null
        const responsible = value ? userUuid : null;

        // Ставим в userContext флаг
        setShowMy(value);

        // Ставим фильтры
        setFilters(prevState => {
            if (prevState) {
                return { ...prevState, responsible }
            } else {
                const newFilters: ClaimListFilters = {
                    responsible,
                    createdAtFrom: null,
                    createdAtTo: null,
                    types: []
                };
                return newFilters
            }
        })
        // При изменении свитча, переходим на первую страницу
        setPage(1);
        // Инвалидируем выборку по заявкам
        queryClient.invalidateQueries({ queryKey: ['travelClaims', statusFilter, ordering, searchQuery, filters, page] })
    }
    const handleShowNew = (value: boolean) => {
        // Если установлен true, то передаём в фильтры status=New, иначе - null
        const status = value ? 'NEW' : undefined;

        // Ставим в userContext флаг
        setShowNew(value);

        // Ставим фильтры
        setFilters(prevState => {
            if (prevState) {
                return { ...prevState, status }
            } else {
                const newFilters: ClaimListFilters = {
                    status,
                    responsible: null,
                    createdAtFrom: null,
                    createdAtTo: null,
                    types: []
                };
                return newFilters
            }
        })
        // При изменении свитча, переходим на первую страницу
        setPage(1);
        // Инвалидируем выборку по заявкам
        queryClient.invalidateQueries({ queryKey: ['travelClaims', statusFilter, ordering, searchQuery, filters, page] })
    }

    // Всплывающее окошко с комментарием START vvv
    const [visibleHint, setVisibleHint] = useState<number | null>(null);
    const [hintPosition, setHintPosition] = useState({ x: 0, y: 0 });
    const [isHoveringRow, setIsHoveringRow] = useState(false);
    const [isHoveringHint, setIsHoveringHint] = useState(false);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleRowMouseEnter = () => {
        setIsHoveringRow(true);
    };

    const handleRowMouseLeave = () => {
        setIsHoveringRow(false);
        if (!isHoveringHint) {
            setVisibleHint(null);
        }
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
        }
    };

    const handleRowMouseMove = (claim_pk: number, event: React.MouseEvent<HTMLDivElement>) => {
        if (isHoveringRow && !visibleHint) {
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
            }
            hoverTimer.current = setTimeout(() => {
                setHintPosition({ x: event.clientX, y: event.clientY });
                setVisibleHint(claim_pk);
            }, 500); // Время зависания курсора над строкой перед открытием коммента
        }
    };

    const handleHintMouseEnter = () => {
        setIsHoveringHint(true);
    };

    const handleHintMouseLeave = () => {
        setIsHoveringHint(false);
        if (!isHoveringRow) {
            setVisibleHint(null);
        }
    };

    const handleDeleteComment = (claim_pk: number) => {
        updateClaim({
            pk: claim_pk.toString(),
            model: {
                responsible_comment: ''
            }
        })
        setVisibleHint(null);
    }

    useEffect(() => {
        return () => {
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
            }
        };
    }, []);
    // Всплывающее окошко с комментарием END ^^^

    // WS START vvv
    const [wsMessage, setWsMessage] = useState<WebsocketMessageData>();
    const invalidateClaims = () => {
        queryClient.invalidateQueries({
            queryKey: ['travelClaims'],
            refetchType: 'all',
        });
    }

    // Для индкатора непрочитанных уведомлений
    const invalidateUnread = () => {
        queryClient.invalidateQueries({
            queryKey: ['unreadNotifications'],
        });
    }

    // Для списка уведомлений
    const invalidateNotifications = () => {
        queryClient.invalidateQueries({
            queryKey: ['notifications'],
        });
    }

    const setClaimUnreadCount = (claim_pk: any, count: number) => {
        // пишем точное число во ВСЕ кэши списка любой фильтр страница и в открытую заявку
        // иммутабельно и сравнение по строке чтобы не зависеть от типа pk и активного ключа
        queryClient.setQueriesData({ queryKey: ['travelClaims'] }, (oldData: any) => {
            if (!oldData?.results) return oldData;
            return {
                ...oldData,
                results: oldData.results.map((item: any) =>
                    String(item.pk) === String(claim_pk) ? { ...item, unread_messages: count } : item
                ),
            };
        });
        queryClient.setQueryData(['travelClaim'], (oldData: any) =>
            oldData && String(oldData.pk) === String(claim_pk) ? { ...oldData, unread_messages: count } : oldData
        );
    };


    useEffect(() => {
        switch (wsMessage?.msg_type) {
            case WebsocketMessagesTypes.NOTIFICATIONS_UPDATE:
                invalidateUnread();
                invalidateNotifications();
                break;
            case WebsocketMessagesTypes.CLAIMS_UPDATE:
                // На данный момент Реалтайм обновление работает, когда invalidate всего списка без фильтров + refetch
                // Серер не сразу выдаёт новую заявку в запросе, поэтому надо поставить задержку
                setTimeout(() => {
                    invalidateClaims();
                    refetchClaims();
                }, 4000)
                break;

            case WebsocketMessagesTypes.CLAIM_UPDATE: // Обновление заявки в общий канал
                // console.log(wsMessage)
                break;
            case WebsocketMessagesTypes.CLAIM_UNREAD_COUNT: // бэк прислал точный счётчик персонально
                if (wsMessage.data?.claim_pk != null) {
                    setClaimUnreadCount(wsMessage.data.claim_pk, +wsMessage.data.unread_messages)
                }
                break;
            default:
                break;
        }
    }, [wsMessage]);
    // WS END ^^^

    // Сбор ид всех пассажиров из заявок и загрузка данных по пользователям
    useEffect(() => {
        if (data?.results && data.results.length) {
            const uniqueUuids = new Set<string>();

            data.results.forEach(item => {
                item.colleagues.forEach((c: any) => {
                    const pk = typeof c === "string" ? c : c?.pk;
                    if (pk) {
                        uniqueUuids.add(pk);
                    }
                });
            });
            setUsersPkOnPage(Array.from(uniqueUuids))
        }
    }, [data]);

    // Если есть список pk от коллег на странице, то загружаем данные по ним
    const {
        data: users,
        isLoading: isLoadingUsers,
        isSuccess: isSuccessUsers
    } = useUsersByIdListQuery(usersPkOnPage);

    // Поиск

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        refetchClaims(); // Заново запрашиваем
    }
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value)
        debouncedSearch(event.target.value, handleSearch);
    }

    const errorPlaceholderView = () => {
        return (
            <div className="h-full flex flex-col justify-center items-center gap-6">
                <div className="flex flex-col justify-center items-center">
                    <EmptyPlaceholder text={'Не удалось подключиться к серверу'} />
                </div>
                <button
                    onClick={() => refetchClaims()}
                    className={
                        'inline-flex items-center justify-center bg-melon-yellow rounded-lg gap-2.5 px-5 py-4 relative font-manager-acrom-ph1-18-medium-acrom font-[number:var(--manager-acrom-ph1-18-medium-acrom-font-weight)] text-[length:var(--manager-acrom-ph1-18-medium-acrom-font-size)] text-center tracking-[var(--manager-acrom-ph1-18-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-ph1-18-medium-acrom-line-height)] [font-style:var(--manager-acrom-ph1-18-medium-acrom-font-style)]'}
                >
                    Попробуйте еще раз
                </button>
            </div>
        );
    };

    return <div className='w-full flex flex-col gap-2 rounded-b-lg overflow-hidden'>
        {/*ОБРАБОТЧИК СОКЕТОВ ОБЩЕГО КАНАЛА*/}
        <CommonWebsocket
            setWsMessageHandler={setWsMessage}
        ></CommonWebsocket>
        {/*ПЕРСОНАЛЬНЫЙ КАНАЛ СЧЁТЧИК НЕПРОЧИТАННЫХ*/}
        <UserWebsocket
            setWsMessageHandler={setWsMessage}
        ></UserWebsocket>
        {/*ЗАГОЛОВОК СТРАНИЦЫ*/}
        <div className='flex justify-between mt-[7px]'>
            <div className='flex text-xl my-auto'>
                {getPageName()}
            </div>

            <div className='flex flex-row justify-center items-center gap-2'>
                {/*Кнопки по показу только новых отображать только на общей вкладке*/}
                {!statusFilter && <AllOrNewCheckbox onChange={handleShowNew} />}

                <div className="bg-white rounded-lg h-12 px-4 flex flex-row justify-center items-center gap-2">
                    <div>
                        Показать свои
                    </div>
                    <RoundSlider value={showMyClaims} onChange={handleShowMy} />
                </div>
                <div className="bg-white rounded-lg h-12 flex flex-row justify-center items-center">
                    <input
                        className='search-input rounded-l-lg h-full w-full focus:outline-none p-3'
                        value={searchText}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearch(searchText);
                        }}
                    />
                    <img
                        className='bg-white p-3 cursor-pointer'
                        src={LookingGlass}
                        alt='search'
                        onClick={() => handleSearch(searchText)}
                    />
                </div>
                {/* ТЕКУЩИЙ ПОПАП ФИЛЬТРОВ ЭТО НЕ ФИЛЬТРЫ, ЭТО ПОПАП В КОНТЕКСТНОМ МЕНЮ НА КОЛОНКЕ ОТВЕТСТВЕННЫЙ */}
                {/*<img*/}
                {/*    className='bg-white p-3 rounded-lg ml-[10px] cursor-pointer'*/}
                {/*    src={FilterIcon}*/}
                {/*    alt='filter'*/}
                {/*    onClick={() => filtersPopupRef?.current?.togglePopup()}*/}
                {/*/>*/}
            </div>
        </div>

        {isLoading ? <Loading /> : isSuccess ?

            <div className='overflow-auto max-h-lvh rounded-t-lg flex flex-col grow'>
                <TravelClaimListContextMenu
                    toggleIsImportant={toggleClaimIsImportant}
                    toggleIsAbroad={toggleClaimIsAbroad}
                    toggleNonresidentColleagues={toggleClaimNonresidentColleagues}
                    updateComment={updateComment}
                    returnToNew={returnClaimToNew}
                    postponeClaim={postponeClaim}
                    setInWork={setClaimInWork}
                    ref={contextMenuRef}
                />
                <ColleaguePopupAvatarList
                    ref={colleaguesListPopupRef}
                />
                {data && data.count ?
                    <div className="flex flex-col h-full justify-between">
                        <div className="block max-h-fit overflow-y-scroll">
                            <table className='table-auto w-full max-h-20 text-left bg-white border-collapse'>
                                <thead className='sticky top-0 bg-white z-10'>
                                    <tr className='font-[16px] text-melon-grey-darker bg-white shadow-below'>
                                        {TABLE_HEAD.map((head, index) => (
                                            <th key={index}>
                                                <div className='flex flex-row justify-center items-center'>
                                                    {head[0]}
                                                    {head[1] ?
                                                        <img
                                                            className='w-[7px] h-[12px] rounded-lg ml-[10px] cursor-pointer inline-block'
                                                            src={getOrderingIcon(head[1])}
                                                            alt='ordering'
                                                            onClick={() => updateOrdering(head[1])}
                                                        />
                                                        :
                                                        <></>
                                                    }
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                {data?.results ?
                                    <tbody className='h-fit'>
                                        {data.results.map((claim: TravelClaimForList) => {
                                            return (
                                                <tr
                                                    className={
                                                        'cursor-pointer border-b-[1px] mx-5 ' +
                                                        (focusedClaim === claim.pk ? 'bg-melon-light-darker ' : ' ') +
                                                        (claim.status === 'NEW' ? 'children:font-bold bg-[#0064FF14] ' : ' ') // Новые заявки жирным шрифтом и голубым фоном
                                                    }
                                                    key={claim.pk}
                                                    onClick={() => navigate(`/claims/${claim.pk}/`)}
                                                    onContextMenu={(e) => {
                                                        e.preventDefault();

                                                        const windowHeight = window.innerHeight;
                                                        const menuHeight = 250
                                                        const offsetWidth = 364
                                                        const clickY = e.clientY;
                                                        const clickX = e.clientX;

                                                        let menuY = clickY;
                                                        let menuX = clickX;

                                                        if (clickY + menuHeight > windowHeight) {
                                                            menuY = clickY - menuHeight;
                                                        }
                                                        if (clickX + offsetWidth > window.innerWidth) {
                                                            menuX = clickX - offsetWidth;
                                                        }

                                                        setFocusedClaim(claim.pk);
                                                        contextMenuRef?.current?.show(menuX, menuY, claim);
                                                    }}
                                                    onMouseEnter={handleRowMouseEnter}
                                                    onMouseLeave={handleRowMouseLeave}
                                                    onMouseMove={(e) => handleRowMouseMove(claim.pk, e)}
                                                >
                                                    <td>
                                                        {/*Всплывающее окошко комментария*/}
                                                        {visibleHint === claim.pk && claim.responsible_comment && (
                                                            <div
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseEnter={handleHintMouseEnter}
                                                                onMouseLeave={handleHintMouseLeave}
                                                                style={{
                                                                    top: hintPosition.y - 10,
                                                                    left: hintPosition.x - 10,
                                                                    zIndex: 1000,
                                                                }}
                                                                className="shadow-lg fixed flex flex-col max-w-[52rem] w-auto bg-white rounded-lg"
                                                            >
                                                                <div
                                                                    className="rounded-t-lg shadow-below p-4 h-12 flex flex-row justify-between items-center">
                                                                    <div className="text-melon-grey">Комментарий</div>
                                                                    <button onClick={() => handleDeleteComment(claim.pk)}>
                                                                        <img src={DeleteIcon} alt="Delete" />
                                                                    </button>
                                                                </div>
                                                                <div
                                                                    className="max-h-40 h-auto p-4 text-melon-black overflow-y-auto">
                                                                    {claim.responsible_comment}
                                                                </div>

                                                            </div>
                                                        )}
                                                        <img
                                                            alt="Bell"
                                                            onMouseMove={handleRowMouseLeave} // Если мышь заходит на колокольчик, то не показывать всплавающий коммент
                                                            className='inline mr-1'
                                                            src={claim.important ? BellYellow : BellGrey}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleClaimIsImportant(claim)
                                                            }}
                                                        />
                                                        {claim.pk}
                                                        {claim.responsible_comment && <img
                                                            alt="Comment"
                                                            className='inline ml-1'
                                                            src={MessagesBlackIcon}
                                                        />}
                                                    </td>
                                                    <td>{getClaimName(claim)}</td>
                                                    <td>
                                                        {getClaimDates(claim)}
                                                    </td>
                                                    <td>
                                                        {getUnreadMessages({
                                                            count: claim.unread_messages,
                                                            claimStatus: claim.status
                                                        })}
                                                    </td>
                                                    <td
                                                        onClick={(e) => {
                                                            // Todo: возможно переделать на onHover
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            colleaguesListPopupRef?.current?.show(
                                                                e.clientX, e.clientY,
                                                                filterUsersByIds(users?.results, claim.colleagues),
                                                            );
                                                        }}
                                                    >
                                                        {claim?.colleagues && users &&
                                                            <ColleagueInlineAvatarList
                                                                users={filterUsersByIds(users?.results, claim.colleagues)}
                                                            />}
                                                        {claim.nonresident_colleagues ?
                                                            <img
                                                                className='inline w-[24px]'
                                                                src={ISIcon}
                                                                alt='Nonresident colleagues'
                                                            />
                                                            :
                                                            <></>
                                                        }
                                                    </td>
                                                    <td>{claim.author && getUserFioShort(claim.author)}</td>
                                                    <td>{new Date(claim.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        {getClaimStatus(claim.status)}
                                                    </td>
                                                    <td>{claim.responsible ? getUserFioShort(claim.responsible) :
                                                        <div className="text-melon-grey text-sm font-normal ">Нет
                                                            ответственного</div>}</td>
                                                    <td className='flex'>
                                                        <img
                                                            className={'h-2 mr-[6px] my-auto ' + getTypeClass(claim.type)}
                                                            src={Circle}
                                                            alt='Claim type color circle'
                                                        />
                                                        <div className='grow my-auto'>
                                                            {getClaimTypeNameShort(claim.type)}
                                                        </div>
                                                        {claim.is_abroad ?
                                                            <img
                                                                className='w-[24px]'
                                                                src={GlobePlaneIcon}
                                                                alt='Is abroad'
                                                            />
                                                            :
                                                            <></>
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    :
                                    <></>
                                }
                            </table>
                        </div>

                        <div className="flex min-h-16 overflow-x-auto sm:justify-center">
                            <Pagination
                                currentPage={page}
                                totalPages={Math.ceil(data.count / PAGE_SIZE)}
                                onPageChange={(page: number) => setPage(page)}
                                previousLabel="Назад"
                                nextLabel="Далее"
                                theme={{
                                    "pages": {
                                        "selector": {
                                            "active": "bg-melon-yellow",
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    :
                    <div className='flex grow content-center'>
                        <EmptyPlaceholder text='Заявок не найдено' />
                    </div>
                }
            </div>
            : errorPlaceholderView()
        }
    </div>
};
